/* eslint-disable @typescript-eslint/brace-style */
import { useNavigate, useLocation } from 'react-router-dom';
import { UserProfile } from 'api/account/models/UserProfile';
import { Paged } from 'api/common/types/Page';
import TrailersService from 'api/trailers/TrailersService';
import { TrailerDto } from 'api/trailers/models/TrailerDto';
import { TrailerSearchCriteria } from 'api/trailers/models/TrailerSearchCriteria';
import UsersService from 'api/users/UsersService';
import ListingTable, { ListingTableColumn } from 'common/components/listingTable/ListingTable';
import PaginationWithInfo from 'common/components/pagination/PaginationWithInfo';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import Logger from 'common/services/Logger';
import { DEFAULT_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE } from 'Config';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import styles from './TrailersScreen.module.scss';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import toast from 'react-hot-toast';
import { Button, Container, Dropdown, Stack, Form, Card, Breadcrumb } from 'react-bootstrap';
import Loading from 'common/services/Loading';

type Props = {
};

const TrailersScreen: React.FC<Props> = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [criteria, setCriteria] = useState<TrailerSearchCriteria>({
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        orderBy: 'asc',
        orderColumn: 't.license_plate'
    });
    const [trailersPage, setTrailersPage] = useState<Paged<TrailerDto>>();
    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasTrailersWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['MY_TRAILERS_WRITE']);
    const [filters, setFilters] = useState({
        allIn: ''
    });
    const getData = async () => {
        try {
            Loading.show();
            const page = await TrailersService.getList(criteria);
            setTrailersPage(page);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get Trailers list', error);
            toast.error(t('messages.error_load_info'));
        } finally {
            Loading.hide();
        }
    }

    const renderTableActionCell = (row: TrailerDto) => {
        return <Dropdown>
            <Dropdown.Toggle id="dropdown-basic-button" size="sm" variant="outline-secondary">...</Dropdown.Toggle>
            <Dropdown.Menu popperConfig={{ strategy: 'fixed' }} renderOnMount>
                <Dropdown.Item onClick={() => navigate(`/trailer/details/${row.id}`)}>{t('common.details')}</Dropdown.Item>
                { hasTrailersWritePolicy && <>
                    <Dropdown.Item onClick={() => navigate(`/trailer/edit/${row.id}`)}>{t('common.edit')}</Dropdown.Item>
                    <Dropdown.Item onClick={() => showRemoveItemDialog(row)}>{t('common.delete')}</Dropdown.Item>
                </>
                }
            </Dropdown.Menu>
        </Dropdown>
    };

    const onTableFilter = (field: string, isFilterAsc: boolean) => {
        setCriteria({ ...criteria, page: 1, orderBy: (isFilterAsc ? 'asc' : 'desc'), orderColumn: field });
    }

    const tableColumns: ListingTableColumn<TrailerDto>[] = [
        {
            field: 'licensePlate',
            name: t('trailers.list.license_plate') ?? '',
            onSearch: onTableFilter,
            searchField: 't.license_Plate'
        },
        {
            field: 'designation',
            name: t('trailers.list.designation') ?? '',
            onSearch: onTableFilter,
            searchField: 't.designation'
        },
        {
            field: 'tagCode',
            name: t('trailers.list.tag') ?? '',
            onSearch: onTableFilter,
            searchField: 'tg.code'
        },
        {
            name: '',
            width: '60px',
            cellAlignment: 'right',
            preventClick: true,
            renderCell: renderTableActionCell,
            cellStyle: { overflow: 'unset' }
        },
    ];

    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
    const [itemToRemove, setItemToRemove] = useState<TrailerDto | null>(null);

    const showRemoveItemDialog = async (item: TrailerDto) => {
        setItemToRemove(item);
        setShowRemoveModal(true);
    }

    const onRowClick = (event: any, id?: string) => {
        if (id) {
            if (event.ctrlKey) {
                window.open(location.pathname + '/details/' + id, '_blank');
                return;
            }
            navigate(`/trailer/details/${id}`);
        }
    }

    const onCancelRemove = () => {
        setItemToRemove(null);
        setShowRemoveModal(false);
    };

    const onRemove = async () => {
        if (itemToRemove === null) {
            toast.error(t('messages.record_delete_error'));
            return;
        }
        try {
            await TrailersService.remove(itemToRemove);
            onCancelRemove();
            toast.success(t('messages.record_delete_success'));
            setCriteria({ ...criteria, page: 1 })
        }
        catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t delete trailers', error);
            toast.error(t('messages.record_delete_error'));
        }
    };

    useEffect(() => {
        getData().catch(console.error);
    }, [criteria]);

    function handleChange(e: any) {
        const key = e.target.name;
        const value = e.target.value;
        setFilters({ ...filters, [key]: value });
    }
    function clearFilters() {
        setFilters({ allIn: '' });
        setCriteria({ ...criteria, page: 1, allIn: '' });
    }
    const onSearch = () => {
        setCriteria({ ...criteria, allIn: filters.allIn });
    }

    return (
        <ScreenTitle title={t('trailers.title')}>
            <Breadcrumb>
                <Breadcrumb.Item active>{t('trailers.title')}</Breadcrumb.Item>
            </Breadcrumb>
            <Container fluid>
                <Card className={styles.filters}>
                    <Card.Body>
                        <Stack direction="horizontal" gap={3} >
                            <Form.Control type="text" value={filters.allIn} className="me-auto" name="allIn" onChange={handleChange} onKeyDown={(e: any) => { if (e.key === 'Enter') onSearch(); }} placeholder={t('common.write_here')} />
                            <Button variant="secondary" onClick={onSearch}>{t('common.search')}</Button>
                            <div className="vr" />
                            <Button variant="outline-danger" onClick={clearFilters}>{t('common.clear')}</Button>
                            <Button variant="primary" onClick={() => navigate('/trailer/new/')}>{t('common.new')}</Button>
                        </Stack>
                    </Card.Body>
                </Card>
            </Container>
            <Container fluid>
                <ListingTable
                    columns={tableColumns}
                    rows={trailersPage?.items || []}
                    onRowClick={(row, _, event) => onRowClick(event, row.id)}
                    allowHover={true}
                    initialSearch={{ colField: 't.code', isOrderAsc: true }}
                />
                <PaginationWithInfo
                    itemName={t('trailers.title')}
                    currentPage={trailersPage?.currentPage ?? 1}
                    pageItems={trailersPage?.items.length || 0}
                    totalItems={trailersPage?.totalItems || 0}
                    onChange={page => setCriteria({ ...criteria, page })}
                />
            </Container>
            <QuestionYesNo onNo={onCancelRemove} onYes={onRemove} isVisible={showRemoveModal} message={t('messages.remove_record_with_ident', { name: itemToRemove?.licensePlate ?? '' })} />
        </ScreenTitle>
    );
};

export default TrailersScreen;
